import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { LightningBoltIcon } from '@/components/icons/lightning-bolt';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import { useOpeningContext } from '@/contexts/opening-context';
import { cn } from '@/utils/classnames';
import { isWithinLast24Hours, toDate } from '@/utils/date-util';
import { parseCurrency } from '@/utils/parse-currency';

import { PerformanceStatusBanner } from '../performance-status-banner';
import { useChannelsStore } from '../use-channels-store';
import { BUDGET_THRESHOLD_PERCENTAGE } from './constants';
import { useAutomationMessages } from './use-automation-messages';

function AutomationStatusBanner() {
  const {
    hiringGoal,
    analyticsData,
    hasActiveHiringGoal,
    hiringGoalUrl,
    setBudgetAutomationBannerColor,
  } = useOpeningContext();
  const channel = useChannelsStore(s => s.lastAutomationChannel);
  const { getAutomationMessages } = useAutomationMessages();
  const { openEditTargetsDialog } = useEditTargetsContext();

  const lastAutomationReason = channel?.deactivation_reason ?? null;
  const isActive = !channel?.deactivated_at;
  const lastAutomationTriggerAt = toDate(
    channel?.automation_triggered_at ?? null,
  );
  const bannerColor = isActive ? 'blue' : 'rose';

  /* Open edit targets dialog on click */
  const handleEditTargets = useCallback(() => {
    openEditTargetsDialog({
      targets: {
        conversion: analyticsData?.conversion_stats?.expected ?? null,
        budget: analyticsData?.budget_stats?.expected ?? null,
        cpa: analyticsData?.cpa_stats?.expected ?? null,
      },
      spendStats: analyticsData?.spend_stats,
    });
  }, [openEditTargetsDialog, analyticsData]);

  /* Check if the spend has breached the 80% threshold of the budget, and is less than the budget */
  const hasSpendBreachedThreshold = useMemo(() => {
    const budget = parseCurrency(analyticsData?.budget_stats?.expected ?? '0');
    const spend = analyticsData?.spend_stats?.total ?? 0;

    if (!budget || !spend) return false;

    return spend < budget && spend > budget * BUDGET_THRESHOLD_PERCENTAGE;
  }, [analyticsData]);

  /* Set the banner color for budget automation banner. This is used to determine the color of the budget card in the analytics section. */
  useEffect(() => {
    if (
      hasSpendBreachedThreshold ||
      lastAutomationReason === 'target_budget_met'
    ) {
      setBudgetAutomationBannerColor(bannerColor);
    } else {
      setBudgetAutomationBannerColor(undefined);
    }
  }, [
    hasSpendBreachedThreshold,
    bannerColor,
    setBudgetAutomationBannerColor,
    lastAutomationReason,
  ]);

  /* Get automation messages */
  const automationCopy = useMemo(() => {
    // If the spend has breached the 80% threshold, we should show the breached banner. This is a custom reason for the banner to show when the budget is breached. Other reasons are from the backend.
    const automationReason =
      !lastAutomationReason && hasSpendBreachedThreshold
        ? 'target_budget_breached'
        : lastAutomationReason;

    const copy = getAutomationMessages(
      automationReason,
      lastAutomationTriggerAt,
    );

    if (!copy) return null;

    const title = isActive ? copy.activeTitle : copy.pausedTitle;
    const description = isActive
      ? copy.activeDescription
      : copy.pausedDescription;

    return {
      title,
      description,
    };
  }, [
    hasSpendBreachedThreshold,
    lastAutomationReason,
    getAutomationMessages,
    lastAutomationTriggerAt,
    isActive,
  ]);

  /* Button logic */
  const button = useMemo(() => {
    if (
      lastAutomationReason === 'hiring_goal_ended' &&
      !isActive &&
      hiringGoalUrl
    ) {
      return (
        <Button variant="secondary" asChild>
          <Link to={String(hiringGoalUrl)} target="_blank">
            <FormattedMessage defaultMessage="Set new hiring goal" />
          </Link>
        </Button>
      );
    }

    if (lastAutomationReason === 'target_budget_met' && !isActive) {
      return (
        <Button variant="secondary" onClick={handleEditTargets}>
          <FormattedMessage defaultMessage="Update Budget" />
        </Button>
      );
    }

    return null;
  }, [lastAutomationReason, isActive, hiringGoalUrl, handleEditTargets]);

  /* For past hiring goal we should not show funnel/budget automation messages. We should show the hiring goal status banner instead. <PerformanceStatusBanner /> */
  if (!hiringGoal?.is_current && lastAutomationReason !== 'hiring_goal_ended') {
    return <PerformanceStatusBanner />;
  }

  /* If the automation was not triggered in the last 24 hours, we should not show the banner. */
  if (
    !hasSpendBreachedThreshold &&
    (!lastAutomationTriggerAt ||
      !(
        lastAutomationTriggerAt && isWithinLast24Hours(lastAutomationTriggerAt)
      ))
  ) {
    return null;
  }

  /* For past hiring goal we should not show hiring automation message if there is an active hiring goal present. */
  if (
    lastAutomationReason === 'hiring_goal_ended' &&
    !isActive &&
    hasActiveHiringGoal
  ) {
    return null;
  }

  /* If there is no automation copy, we should not show the banner. */
  if (!automationCopy) {
    return null;
  }

  return (
    <Banner
      color={bannerColor}
      title={automationCopy.title}
      description={automationCopy.description}
      button={button}
      icon={
        <LightningBoltIcon
          className={cn('h-4 w-4', isActive ? 'text-fx-blue' : 'text-fx-rose')}
        />
      }
    />
  );
}

export { AutomationStatusBanner };
